import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import sanitizeHtml from "sanitize-html";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import "./CommunityTableDetails.css";

function CommunityTableDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [steamDescription, setSteamDescription] = useState("");
  const [steamHeaderUrl, setSteamHeaderUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTable, setReportTable] = useState(null);
  const [reportReason, setReportReason] = useState("not_working");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState("");

  async function submitReport() {
    if (!reportTable?._id) return;
    setReportBusy(true);
    setReportMsg("");
    try {
      await axios.post(
        `/api/community/tables/${reportTable._id}/report`,
        { reason: reportReason, details: reportDetails },
        {
          headers: localStorage.getItem("token")
            ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
            : {},
        }
      );
      setReportMsg("Report submitted. Thank you!");
      setReportDetails("");
    } catch (e) {
      setReportMsg(e?.response?.data?.error || "Failed to submit report.");
    } finally {
      setReportBusy(false);
    }
  }

  useEffect(() => {
    const fetchSteamDescription = async (appid) => {
      const response = await axios.get(`/api/requests/steam-description/${appid}`, {
        params: { language: 'en', t: Date.now() },
        headers: { 'Accept-Language': 'en' }
      });
      return response.data.description || "No description available.";
    };

    const resolveSteamHeader = async (appid) => {
      if (!appid) { setSteamHeaderUrl(""); return; }
      if (Number(appid) === 2623190) {
        setSteamHeaderUrl("https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/2623190/a7cee9165bb1bfc092c390c5cff215ce0e381dfc/header.jpg");
        return;
      }
      const primary = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${appid}/header.jpg`;
      try {
        await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = primary;
        });
        setSteamHeaderUrl(primary);
      } catch {
        try {
          const details = await axios.get(`/api/steam/appdetails/${appid}`);
          if (details.data?.header_image) setSteamHeaderUrl(details.data.header_image);
          else setSteamHeaderUrl("");
        } catch {
          setSteamHeaderUrl("");
        }
      }
    };

    const fetchData = async () => {
      try {
        const categoryResponse = await axios.get(`/api/community/category/slug/${slug}`);
        const categoryData = categoryResponse.data;
        if (!categoryData) throw new Error("Category not found");
        setCategory(categoryData);

        if (categoryData.appid) {
          const rawDescription = await fetchSteamDescription(categoryData.appid);
          const sanitizedDescription = sanitizeHtml(rawDescription, {
            allowedTags: ['p','b','i','em','strong','a','ul','ol','li','br','div','span','h1','h2','h3','h4','h5','h6','img'],
            allowedAttributes: { 'a': ['href','target','rel'], 'img': ['src','alt','width','height'] },
            transformTags: {
              img: (tagName, attribs) => {
                if (attribs.src && !attribs.src.startsWith('http')) {
                  attribs.src = `https://${attribs.src}`;
                }
                return { tagName, attribs };
              }
            }
          });
          setSteamDescription(sanitizedDescription);
          await resolveSteamHeader(categoryData.appid);
        } else {
          setSteamDescription("No description available.");
          setSteamHeaderUrl("");
        }
      } catch (err) {
        setError(err.message || "Failed to load category details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const handleBack = () => {
    navigate("/games");
  };

  const handleCommunityTableDownload = async (table) => {
    try {
      setActionLoading((prev) => ({ ...prev, [`download-${table._id}`]: true }));
      const response = await axios.get(`/api/community/download-table/${table._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${category.name.replace(/\s+/g, '_')}-v${table.version || '1.0'}.ct`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setError("");

      const categoryResponse = await axios.get(`/api/community/category/slug/${slug}`);
      if (categoryResponse.data) setCategory(categoryResponse.data);
    } catch (err) {
      setError(err.response?.data?.message || "Error downloading table");
    } finally {
      setActionLoading((prev) => ({ ...prev, [`download-${table._id}`]: false }));
    }
  };

  if (loading) return <div className="tables-page-container"><p>Loading...</p></div>;

  if (error) {
    return (
      <div className="tables-page-container">
        <p className="status" style={{ color: "red" }}>{error}</p>
        <button className="back-button" onClick={handleBack}>Back to Categories</button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="category-detail-container">
        <Helmet>
          <title>{`${category.name} - Community Tables`}</title>
          <meta name="description" content={`View community cheat tables for ${category.name}.`} />
        </Helmet>

        <div className="banner">
          <h2 className="banner-title">{category.name}</h2>
        </div>

        <div className="disclaimer">
          <p>
            Disclaimer: These community tables are created and managed by their respective creators. We verify and host these tables but are not responsible for their content, updates, or any loss of account, data, or damage resulting from their use. Use at your own risk.
          </p>
        </div>
        <hr className="divider" />

        <div className="category-content">
          <div className="category-media">
            {steamHeaderUrl && (
              <div className="steam-header-wrap">
                <img className="steam-header" src={steamHeaderUrl} alt={`${category.name} header`} />
              </div>
            )}

            {/* New callout moved here, right above the downloads */}
            <div className="contribute-callout">
              <span className="contribute-text">Would you like to upload your own table?</span>{" "}
              <a className="contribute-link" href="/community-tables">Visit the community tables page</a>.
            </div>

            <div className="download-section">
              <h3>Available Tables</h3>
              {category.tables && category.tables.length > 0 ? (
                category.tables
                  .filter((table) => table.status === "approved")
                  .map((table) => (
                    <div key={table._id} className="table-item">
                      <div className="table-header">
                        <div className="table-meta">
                          <span><strong>Uploader:</strong> {table.submitterId?.username || "Unknown"}</span>
                          <span><strong>Uploaded:</strong> {new Date(table.createdAt).toLocaleDateString()}</span>
                          <span><strong>Version:</strong> {table.version || "1.0"}</span>
                          <span><strong>Downloads:</strong> {table.downloadCount || 0}</span>
                        </div>
                        <button
                          onClick={() => handleCommunityTableDownload(table)}
                          className="download-link"
                          disabled={actionLoading[`download-${table._id}`]}
                          data-tooltip="Download .ct file"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          {actionLoading[`download-${table._id}`] ? " Downloading..." : " Download"}
                        </button>
                        <button
                          type="button"
                          className="btn-danger"
                          onClick={() => { setReportTable(table); setReportOpen(true); }}
                          title="Report this table"
                        >
                          REPORT
                        </button>
                      </div>
                      <p className="table-description">{table.description || "No description provided."}</p>
                    </div>
                  ))
              ) : (
                <p>No approved tables available.</p>
              )}
            </div>
          </div>

          <div className="category-description">
            <div className="description-content">
              <p>
                Welcome to our <strong>{category.name}</strong> community table page. Here, you’ll find all community-submitted cheat tables for this game.
                These tables are created by our community members and verified for functionality.
              </p>
              <h3>How do I use this table?</h3>
              <p>
                You will need Cheat Engine to run these files. Download it from
                <a href="https://cheatengine.org/" target="_blank" rel="noopener noreferrer"> https://cheatengine.org/ </a>
                or check our resources in the “Programs” section of our Discord.
              </p>
              <p>
                <strong>New to Cheat Engine?</strong> No problem! We offer guides on how to use Cheat Engine,
                complete with easy-to-follow instructions and tips.
              </p>
              <p>
                Download the latest <strong>{category.name}</strong> community cheat table now and enhance your gameplay.
                Explore other community tables for top games.
              </p>
              <p><strong>Note:</strong> Tables are designed for specific game versions.
                Check compatibility details before downloading.
              </p>
              <hr className="divider" />
              {steamDescription && (
                <div className="steam-description" dangerouslySetInnerHTML={{ __html: steamDescription }} />
              )}
            </div>
          </div>
        </div>

        {reportOpen && (
          <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) setReportOpen(false); }}
          >
            <div className="modal-card" role="document">
              <div className="modal-header">
                <h3>Report this Table</h3>
                <button className="modal-close" onClick={() => setReportOpen(false)} aria-label="Close">×</button>
              </div>

              <div className="modal-body">
                <div className="form-field">
                  <label htmlFor="report-reason">Reason</label>
                  <select
                    id="report-reason"
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                  >
                    <option value="not_working">Table not working</option>
                    <option value="breaks_game">Table breaks/bugs game</option>
                    <option value="malicious">Table has malicious functions</option>
                    <option value="stolen">This table is stolen</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-field">
                  <label htmlFor="report-details">Details <span className="tiny muted">(optional)</span></label>
                  <textarea
                    id="report-details"
                    maxLength={1000}
                    placeholder="Add context, steps to reproduce, proof, etc. (max 1000 chars)"
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                  />
                  <div className="tiny muted">{reportDetails.length}/1000</div>
                </div>

                {reportMsg && <div className="alert alert-success">{reportMsg}</div>}
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setReportOpen(false)}>Cancel</button>
                <button className="btn-primary" disabled={reportBusy} onClick={submitReport}>
                  {reportBusy ? "Sending…" : "Send report"}
                </button>
              </div>
            </div>
          </div>
        )}

        <button className="back-button" onClick={handleBack}>Back to Categories</button>
      </div>
    </div>
  );
}

export default CommunityTableDetails;
